import { useTranslation } from "react-i18next";

import {
  CircleCheckIcon,
  CircleHelpIcon,
  CircleQuestionMarkIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { Icon } from "simplydo/react/ui/icon";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

type CheckResult =
  | "yes"
  | "no"
  | "unknown"
  | "none"
  | "issues"
  | "known"
  | "watch"
  | "outstanding"
  | "satisfied";

type SizeBand =
  | "large"
  | "medium"
  | "small"
  | "micro"
  | "dormant"
  | "newlyIncorporated";

type DueDiligenceOverviewData = {
  activelyTrading?: { result?: CheckResult; description?: string };
  ukRegistered?: {
    result?: CheckResult;
    country?: string;
    companyNumber?: string;
    parentName?: string;
    parentCountry?: string;
  };
  size?: {
    result?: CheckResult;
    turnoverCurrency?: string;
    turnoverValue?: number;
    employees?: number;
    band?: SizeBand;
    profileSize?: "micro" | "small" | "medium" | "large";
    reportedSize?: "micro" | "small" | "medium" | "large";
    employeeMatch?: "yes" | "no" | "unknown";
  };
  creditRating?: {
    result?: CheckResult;
    commonValue?: string;
    description?: string;
    score?: number;
    scoreMax?: number;
    limitCurrency?: string;
    limitValue?: number;
    previousScore?: number;
    change?: "up" | "down";
  };
  complianceIssues?: { result?: CheckResult; items?: string[] };
  filingsUpToDate?: {
    result?: CheckResult;
    description?: string;
    reason?: "accountsDuePassed";
    lastFiled?: string;
    accountsDue?: string;
  };
  charges?: { result?: CheckResult; outstanding?: number; satisfied?: number };
  addressMatch?: {
    result?: CheckResult;
    profilePostcode?: string;
    reportPostcode?: string;
  };
  founding?: {
    result?: CheckResult;
    profileYear?: number;
    incorporatedOn?: string;
    reportedStatus?:
      | "startup"
      | "established_small"
      | "established_medium"
      | "established_large";
    yearMatch?: boolean;
  };
};

const positiveResults = new Set<CheckResult>([
  "yes",
  "none",
  "known",
  "satisfied",
]);
const warningResults = new Set<CheckResult>([
  "no",
  "issues",
  "watch",
  "outstanding",
]);

const resultIcon = (result: CheckResult) => {
  if (positiveResults.has(result)) {
    return { icon: CircleCheckIcon, color: "#1e7f4f" };
  }
  if (warningResults.has(result)) {
    return { icon: TriangleAlertIcon, color: "#b45309" };
  }
  return { icon: CircleHelpIcon, color: "#666" };
};

const formatMoney = (currency?: string, value?: number) => {
  if (value == null || Number.isNaN(value)) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency || "GBP"} ${value}`;
  }
};

const formatCount = (value?: number) =>
  value == null ? "" : value.toLocaleString();

const CheckRow = ({
  result,
  title,
  detail,
}: {
  result: CheckResult;
  title: string;
  detail?: string;
}) => {
  const { icon, color } = resultIcon(result);
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <Icon icon={icon} style={{ color, marginTop: 2, flexShrink: 0 }} />
      <div>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {detail ? (
          <div style={{ opacity: 0.8, fontSize: "0.95em" }}>{detail}</div>
        ) : null}
      </div>
    </div>
  );
};

type DueDiligenceOverviewProps = { overview: DueDiligenceOverviewData };

export const DueDiligenceOverview = ({
  overview,
}: DueDiligenceOverviewProps) => {
  const { t } = useTranslation();
  if (!overview) {
    return null;
  }

  const trading = overview.activelyTrading;
  const ukRegistered = overview.ukRegistered;
  const size = overview.size;
  const credit = overview.creditRating;
  const compliance = overview.complianceIssues;
  const filings = overview.filingsUpToDate;
  const charges = overview.charges;
  const address = overview.addressMatch;
  const founding = overview.founding;

  const tradingDetail =
    trading?.result === "unknown"
      ? t("ideas.dueDiligence.trading.unknown")
      : trading?.description ||
        (trading?.result === "yes"
          ? t("ideas.dueDiligence.trading.yes")
          : t("ideas.dueDiligence.trading.no"));

  const registrationDetail = (() => {
    if (!ukRegistered || ukRegistered.result === "unknown") {
      return t("ideas.dueDiligence.ukRegistered.unknown");
    }
    const registration =
      ukRegistered.result === "no"
        ? t("ideas.dueDiligence.ukRegistered.no", {
            country: ukRegistered.country,
          })
        : ukRegistered.companyNumber
          ? t("ideas.dueDiligence.ukRegistered.yes", {
              country: ukRegistered.country,
              companyNumber: ukRegistered.companyNumber,
            })
          : t("ideas.dueDiligence.ukRegistered.yesNoNumber", {
              country: ukRegistered.country,
            });
    const parentOutsideUk =
      ukRegistered.parentCountry &&
      ukRegistered.result === "watch" &&
      ukRegistered.parentName;
    const parent = parentOutsideUk
      ? t("ideas.dueDiligence.ukRegistered.parentNonUk", {
          name: ukRegistered.parentName,
          country: ukRegistered.parentCountry,
        })
      : ukRegistered.parentName && ukRegistered.parentCountry
        ? t("ideas.dueDiligence.ukRegistered.parent", {
            name: ukRegistered.parentName,
            country: ukRegistered.parentCountry,
          })
        : ukRegistered.parentName
          ? t("ideas.dueDiligence.ukRegistered.parentNameOnly", {
              name: ukRegistered.parentName,
            })
          : "";
    return [registration, parent].filter(Boolean).join(" ");
  })();

  const turnover = formatMoney(size?.turnoverCurrency, size?.turnoverValue);
  const employees = formatCount(size?.employees);
  const band = size?.band ? t(`ideas.dueDiligence.size.band.${size.band}`) : "";
  const sizeFigures =
    turnover && employees
      ? t("ideas.dueDiligence.size.turnoverAndEmployees", {
          turnover,
          employees,
        })
      : turnover
        ? t("ideas.dueDiligence.size.turnover", { turnover })
        : employees
          ? t("ideas.dueDiligence.size.employees", { employees })
          : "";
  const sizeBandLabel = (value?: string) =>
    value ? t(`ideas.dueDiligence.size.band.${value}`) : "";
  const employeeCompare =
    size?.employeeMatch === "yes"
      ? t("ideas.dueDiligence.size.employeeMatch", {
          band: sizeBandLabel(size.reportedSize || size.profileSize),
        })
      : size?.employeeMatch === "no"
        ? t("ideas.dueDiligence.size.employeeMismatch", {
            profileBand: sizeBandLabel(size.profileSize),
            reportedBand: sizeBandLabel(size.reportedSize),
            employees,
          })
        : size?.employeeMatch === "unknown"
          ? t("ideas.dueDiligence.size.employeeUnknown", {
              profileBand: sizeBandLabel(size.profileSize),
            })
          : "";
  const sizeDetail = [
    size?.result === "unknown" || (!sizeFigures && !band)
      ? t("ideas.dueDiligence.size.unknown")
      : [sizeFigures, band ? t("ideas.dueDiligence.size.band", { band }) : ""]
          .filter(Boolean)
          .join(" "),
    employeeCompare,
  ]
    .filter(Boolean)
    .join(" ");

  const limit = formatMoney(credit?.limitCurrency, credit?.limitValue);
  const creditSummary =
    credit?.commonValue && credit.score != null
      ? t("ideas.dueDiligence.creditRating.summary", {
          band: credit.commonValue,
          score: credit.score,
          max: credit.scoreMax || 100,
        })
      : credit?.commonValue
        ? t("ideas.dueDiligence.creditRating.summaryNoScore", {
            band: credit.commonValue,
          })
        : credit?.score != null
          ? t("ideas.dueDiligence.creditRating.scoreOnly", {
              score: credit.score,
              max: credit.scoreMax || 100,
            })
          : "";
  const creditDetail =
    !credit || (credit.result === "unknown" && !creditSummary)
      ? t("ideas.dueDiligence.creditRating.unknown")
      : [
          creditSummary,
          credit.description || "",
          limit ? t("ideas.dueDiligence.creditRating.limit", { limit }) : "",
          credit.previousScore != null
            ? t("ideas.dueDiligence.creditRating.previous", {
                previousScore: credit.previousScore,
              })
            : "",
        ]
          .filter(Boolean)
          .join(" ");

  const complianceDetail =
    compliance?.result === "issues"
      ? (compliance.items || []).join("; ")
      : compliance?.result === "none"
        ? t("ideas.dueDiligence.compliance.none")
        : t("ideas.dueDiligence.compliance.unknown");

  const filed = filings?.lastFiled
    ? formatDate(filings.lastFiled, "d MMM yyyy")
    : "";
  const due = filings?.accountsDue
    ? formatDate(filings.accountsDue, "d MMM yyyy")
    : "";
  const passedDueDate = filings?.reason === "accountsDuePassed";
  const filingDates =
    passedDueDate && due
      ? t("ideas.dueDiligence.filings.overdueOn", { due })
      : filed && due
        ? t("ideas.dueDiligence.filings.filedAndDue", { filed, due })
        : filed
          ? t("ideas.dueDiligence.filings.filedOnly", { filed })
          : due
            ? t("ideas.dueDiligence.filings.dueOnly", { due })
            : "";
  const filingsDetail = [
    passedDueDate ? "" : filings?.description,
    filingDates,
    !filings?.description && !filingDates
      ? filings?.result === "yes"
        ? t("ideas.dueDiligence.filings.upToDate")
        : filings?.result === "no"
          ? t("ideas.dueDiligence.filings.overdue")
          : t("ideas.dueDiligence.filings.unknown")
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const chargesDetail =
    charges?.result === "outstanding"
      ? t("ideas.dueDiligence.charges.outstanding", {
          outstanding: charges.outstanding ?? 0,
          satisfied: charges.satisfied ?? 0,
        })
      : charges?.result === "satisfied"
        ? t("ideas.dueDiligence.charges.satisfied", {
            satisfied: charges.satisfied ?? 0,
          })
        : charges?.result === "none"
          ? t("ideas.dueDiligence.charges.none")
          : t("ideas.dueDiligence.charges.unknown");

  const addressDetail =
    address?.result === "yes"
      ? t("ideas.dueDiligence.address.match", {
          postcode: address.profilePostcode || address.reportPostcode,
        })
      : address?.result === "no"
        ? t("ideas.dueDiligence.address.mismatch", {
            profilePostcode: address.profilePostcode || "—",
            reportPostcode: address.reportPostcode || "—",
          })
        : t("ideas.dueDiligence.address.unknown");

  const foundingStatusLabel = (value?: string) =>
    value ? t(`ideas.dueDiligence.founded.status.${value}`) : "";
  const incorporated = founding?.incorporatedOn
    ? formatDate(founding.incorporatedOn, "d MMM yyyy")
    : "";
  const reportedStatus = foundingStatusLabel(founding?.reportedStatus);
  const foundingDetail =
    !founding ||
    founding.result === "unknown" ||
    !incorporated ||
    founding.profileYear == null
      ? t("ideas.dueDiligence.founded.unknown")
      : t(
          founding.yearMatch === false
            ? "ideas.dueDiligence.founded.yearMismatch"
            : "ideas.dueDiligence.founded.match",
          {
            year: founding.profileYear,
            date: incorporated,
            status: reportedStatus,
          },
        );

  return (
    <Message
      size="small"
      style={{
        marginBottom: 24,
        boxShadow:
          "0px 5px 10px 0px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(34,36,38,.22) inset",
      }}
    >
      <MessageHeader
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {t("ideas.dueDiligence.title")}
        <Popup
          offset={[13, 2]}
          trigger={<Icon icon={CircleQuestionMarkIcon} />}
        >
          {t("ideas.dueDiligence.info")}
        </Popup>
      </MessageHeader>
      <Divider fitted style={{ marginTop: 4, marginBottom: 4 }} />
      <Grid columns={2} mobile={1} gap="0.5rem 1rem" style={{ marginTop: 8 }}>
        <GridColumn>
          <CheckRow
            result={trading?.result || "unknown"}
            title={t("ideas.dueDiligence.trading")}
            detail={tradingDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={ukRegistered?.result || "unknown"}
            title={t("ideas.dueDiligence.ukRegistered")}
            detail={registrationDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={size?.result || "unknown"}
            title={t("ideas.dueDiligence.size")}
            detail={sizeDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={credit?.result || "unknown"}
            title={t("ideas.dueDiligence.creditRating")}
            detail={creditDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={compliance?.result || "unknown"}
            title={t("ideas.dueDiligence.compliance")}
            detail={complianceDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={filings?.result || "unknown"}
            title={t("ideas.dueDiligence.filings")}
            detail={filingsDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={charges?.result || "unknown"}
            title={t("ideas.dueDiligence.charges")}
            detail={chargesDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={address?.result || "unknown"}
            title={t("ideas.dueDiligence.address")}
            detail={addressDetail}
          />
        </GridColumn>
        <GridColumn>
          <CheckRow
            result={founding?.result || "unknown"}
            title={t("ideas.dueDiligence.founded")}
            detail={foundingDetail}
          />
        </GridColumn>
      </Grid>
    </Message>
  );
};
